import { Badge, Input, cn } from '@recipes/ui';
import { X } from 'lucide-react';
import { useId, useState, type KeyboardEvent } from 'react';

export interface ChipsInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  /** Suggestions shown below the input (filtered by the typed text). */
  suggestions?: string[];
  placeholder?: string;
  label: string;
  removeLabel: string;
  addLabel?: string;
  maxItems?: number;
  className?: string;
  id?: string;
  disabled?: boolean;
  'data-testid'?: string;
}

/**
 * Free-text chips (tags, avoided ingredients). Enter or comma adds, Backspace on an empty
 * input removes the last chip, suggestions are keyboard reachable as buttons.
 */
export function ChipsInput({
  value,
  onChange,
  suggestions = [],
  placeholder,
  label,
  removeLabel,
  addLabel,
  maxItems = 50,
  className,
  id,
  disabled,
  'data-testid': testId,
}: ChipsInputProps) {
  const [draft, setDraft] = useState('');
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const listId = `${inputId}-suggestions`;

  const add = (raw: string) => {
    const text = raw.trim().replace(/,+$/, '').trim();
    if (!text) return;
    const exists = value.some((v) => v.toLowerCase() === text.toLowerCase());
    if (!exists && value.length < maxItems) onChange([...value, text]);
    setDraft('');
  };
  const remove = (item: string) => onChange(value.filter((v) => v !== item));

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      add(draft);
    } else if (event.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const filtered = suggestions
    .filter((s) => !value.some((v) => v.toLowerCase() === s.toLowerCase()))
    .filter((s) => (draft ? s.toLowerCase().includes(draft.toLowerCase()) : true))
    .slice(0, 8);

  return (
    <div className={cn('space-y-2', className)} data-testid={testId}>
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label={label}>
          {value.map((item) => (
            <li key={item}>
              <Badge variant="secondary" className="gap-1 py-1 pl-2.5 pr-1 text-sm">
                {item}
                <button
                  type="button"
                  className="inline-flex size-6 items-center justify-center rounded-full hover:bg-background/60"
                  onClick={() => remove(item)}
                  aria-label={`${removeLabel}: ${item}`}
                  disabled={disabled}
                >
                  <X className="size-3.5" aria-hidden />
                </button>
              </Badge>
            </li>
          ))}
        </ul>
      )}
      <Input
        id={inputId}
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => add(draft)}
        aria-label={label}
        aria-controls={filtered.length ? listId : undefined}
        autoComplete="off"
        disabled={disabled}
      />
      {filtered.length > 0 && (
        <div
          id={listId}
          className="flex flex-wrap gap-1.5"
          role="group"
          aria-label={addLabel ?? label}
        >
          {filtered.map((s) => (
            <button
              key={s}
              type="button"
              className="rounded-full border px-2.5 py-1 text-xs hover:bg-accent"
              onClick={() => add(s)}
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
